import { Button, Modal, Label, TextInput, Textarea } from "flowbite-react";
import { useForm, Controller } from "react-hook-form";
import { EventRecord } from "@/sharedTypes";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import PaginatedSelect from "@/components/PaginatedSelect";
import { useGetCitiesQuery } from "@/api/locationApi/city";
import { useGetEventTypesQuery } from "@/api/locationApi/eventType";
import { useGetEventStatusesQuery } from "@/api/locationApi/eventStatus";
import EventDistrictSelect from "./EventDistrictSelect";

interface EventFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: EventRecord) => void;
  defaultValues?: EventRecord;
  mode?: "create" | "edit";
  errorMessage?: string;
}

export default function EventFormDialog({
  open,
  onClose,
  onSubmit,
  defaultValues,
  mode = "create",
  errorMessage,
}: EventFormDialogProps) {
  const { t } = useTranslation(["temporalActivity", "common"]);
  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EventRecord>();

  const [cityPage, setCityPage] = useState(1);
  const [citySearch, setCitySearch] = useState("");
  const { data: citiesData, isFetching: isCitiesFetching } = useGetCitiesQuery({
    pagination: { page: cityPage, pageSize: 15 },
    filters: citySearch ? { "city_name[like]": citySearch } : {},
  });
  const cityOptions = (citiesData?.items || []).map((c: any) => ({ label: c.city_name, value: String(c.id) }));

  const { data: eventTypesData } = useGetEventTypesQuery({ pagination: { page: 1, pageSize: 100 } });
  const eventTypeOptions = (eventTypesData?.items || []).map((et: any) => ({
    label: et.event_type,
    value: String(et.id),
  }));

  const { data: eventStatusesData } = useGetEventStatusesQuery({ pagination: { page: 1, pageSize: 100 } });
  const eventStatusOptions = (eventStatusesData?.items || []).map((es: any) => ({
    label: es.status,
    value: String(es.id),
  }));

  const selectedCityId = watch("city_id");

  useEffect(() => {
    if (open) {
      reset(
        defaultValues || {
          event_type_id: "",
          event_status_id: "",
          city_id: "",
          district_ids: [],
          start_date: "",
          end_date: "",
          start_time: "",
          end_time: "",
          title: "",
          details: "",
        }
      );
      setCitySearch("");
      setCityPage(1);
    }
  }, [open, defaultValues, reset]);

  return (
    <Modal show={open} onClose={onClose} size="xl">
      <Modal.Header>
        {mode === "edit" ? t("temporalActivity:event.edit") : t("temporalActivity:event.create")}
      </Modal.Header>
      <Modal.Body className="overflow-visible">
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <div className="mb-2 block">
              <Label htmlFor="title" value={t("temporalActivity:event.eventTitle")} />
            </div>
            <TextInput
              id="title"
              placeholder={t("temporalActivity:event.eventTitlePlaceholder")}
              {...register("title", { required: t("temporalActivity:event.titleRequired") as string })}
              color={errors.title ? "failure" : "gray"}
              helperText={errors.title?.message}
            />
          </div>

          <div>
            <div className="mb-2 block">
              <Label htmlFor="details" value={t("temporalActivity:event.details")} />
            </div>
            <Textarea
              id="details"
              placeholder={t("temporalActivity:event.detailsPlaceholder")}
              {...register("details")}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Controller
              name="event_type_id"
              control={control}
              rules={{ required: t("temporalActivity:event.eventTypeRequired") as string }}
              render={({ field }) => {
                const selected = field.value
                  ? eventTypeOptions.find((o: any) => o.value === String(field.value)) ?? null
                  : null;
                return (
                  <PaginatedSelect
                    id="event_type_id"
                    label={t("temporalActivity:event.eventType")}
                    value={selected}
                    onChange={(val: any) => field.onChange(val?.value ?? "")}
                    options={eventTypeOptions}
                    onInputChange={() => {}}
                    placeholder={t("temporalActivity:event.selectEventType")}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                  />
                );
              }}
            />
            {errors.event_type_id && (
              <p className="text-sm text-red-500">{errors.event_type_id.message as string}</p>
            )}

            <Controller
              name="event_status_id"
              control={control}
              rules={{ required: t("temporalActivity:event.eventStatusRequired") as string }}
              render={({ field }) => {
                const selected = field.value
                  ? eventStatusOptions.find((o: any) => o.value === String(field.value)) ?? null
                  : null;
                return (
                  <PaginatedSelect
                    id="event_status_id"
                    label={t("temporalActivity:event.eventStatus")}
                    value={selected}
                    onChange={(val: any) => field.onChange(val?.value ?? "")}
                    options={eventStatusOptions}
                    onInputChange={() => {}}
                    placeholder={t("temporalActivity:event.selectEventStatus")}
                    page={1}
                    totalPages={1}
                    onPageChange={() => {}}
                  />
                );
              }}
            />
            {errors.event_status_id && (
              <p className="text-sm text-red-500">{errors.event_status_id.message as string}</p>
            )}
          </div>

          <Controller
            name="city_id"
            control={control}
            rules={{ required: t("temporalActivity:event.cityRequired") as string }}
            render={({ field }) => {
              const selected = field.value
                ? {
                    label:
                      cityOptions.find((o: any) => o.value === String(field.value))?.label ??
                      defaultValues?.city?.city_name ??
                      "",
                    value: String(field.value),
                  }
                : null;
              return (
                <PaginatedSelect
                  id="city_id"
                  label={t("temporalActivity:event.city")}
                  value={selected}
                  onChange={(val: any) => field.onChange(val?.value ?? "")}
                  options={cityOptions}
                  onInputChange={(val) => {
                    setCitySearch(val);
                    setCityPage(1);
                  }}
                  isLoading={isCitiesFetching}
                  isFetching={isCitiesFetching}
                  placeholder={t("temporalActivity:event.selectCity")}
                  page={cityPage}
                  totalPages={citiesData?.pagination?.totalPages || 1}
                  onPageChange={setCityPage}
                />
              );
            }}
          />
          {errors.city_id && <p className="text-sm text-red-500">{errors.city_id.message}</p>}

          <Controller
            name="district_ids"
            control={control}
            render={({ field }) => (
              <EventDistrictSelect
                cityId={selectedCityId}
                value={field.value ?? []}
                onChange={(val) => field.onChange(val)}
              />
            )}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="start_date" value={t("temporalActivity:event.startDate")} />
              </div>
              <TextInput
                id="start_date"
                type="date"
                {...register("start_date", { required: t("temporalActivity:event.startDateRequired") as string })}
                color={errors.start_date ? "failure" : "gray"}
                helperText={errors.start_date?.message}
              />
            </div>
            <div>
              <div className="mb-2 block">
                <Label htmlFor="end_date" value={t("temporalActivity:event.endDate")} />
              </div>
              <TextInput
                id="end_date"
                type="date"
                {...register("end_date", { required: t("temporalActivity:event.endDateRequired") as string })}
                color={errors.end_date ? "failure" : "gray"}
                helperText={errors.end_date?.message}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <div className="mb-2 block">
                <Label htmlFor="start_time" value={t("temporalActivity:event.startTime")} />
              </div>
              <TextInput id="start_time" type="time" {...register("start_time")} />
            </div>
            <div>
              <div className="mb-2 block">
                <Label htmlFor="end_time" value={t("temporalActivity:event.endTime")} />
              </div>
              <TextInput id="end_time" type="time" {...register("end_time")} />
            </div>
          </div>

          {errorMessage && <div className="text-sm text-red-500">{errorMessage}</div>}

          <div className="flex justify-end gap-x-2">
            <Button color="gray" onClick={onClose}>
              {t("common:cancel")}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {mode === "edit" ? t("common:save") : t("common:create")}
            </Button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}
